import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { SlackishMarkdown } from '@platform/markdown';
import React from 'react';
import { tokenService } from '../../../services/tokenService';

interface ArtifactViewDialogProps {
  open: boolean;
  onClose: () => void;
  artifactId: string;
  artifactTitle: string;
  profileSlug: string;
  transcriptTitle?: string;
}

interface AdminArtifactResponse {
  id: string;
  title: string;
  profile_slug: string;
  markdown: string;
  created_at: string;
  updated_at: string;
}

export function ArtifactViewDialog({
  open,
  onClose,
  artifactId,
  artifactTitle,
  profileSlug,
  transcriptTitle,
}: ArtifactViewDialogProps): React.ReactElement {
  const [markdown, setMarkdown] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || !artifactId) {
      setMarkdown('');
      setError(null);
      return;
    }

    const fetchArtifact = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const headers = await tokenService.buildAuthHeaders();
        // Use admin endpoint which bypasses owner check
        const res = await fetch(`/api/v1/admin/artifacts/${encodeURIComponent(artifactId)}`, {
          headers,
        });
        if (!res.ok) {
          setError(
            res.status === 404 ? 'Artifact not found.' : `Failed to load artifact (${res.status}).`
          );
          return;
        }
        const data = (await res.json()) as AdminArtifactResponse;
        setMarkdown(data.markdown || '');
      } catch {
        setError('Network error. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchArtifact();
  }, [open, artifactId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pr: 6 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" component="div" sx={{ wordBreak: 'break-word' }}>
            {artifactTitle}
          </Typography>
          {transcriptTitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              From: {transcriptTitle}
            </Typography>
          )}
        </Box>
        <Chip label={profileSlug} size="small" variant="outlined" sx={{ flexShrink: 0 }} />
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box
            className="mp-block artifact-content"
            sx={{ '& .slackish-markdown': { fontSize: '0.95rem' } }}
          >
            <SlackishMarkdown text={markdown} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
